import { createClient } from '@supabase/supabase-js';
import type { Database } from "../../../supabase/database.types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * The console only ever means to query as a signed-in staff member. When a page's
 * fetch chain is still running as the session ends (logout, access denied, expiry),
 * supabase-js falls back to the anon key and every request lands in Postgres as
 * "permission denied for table bookings" (42501). Answer those locally with a 401
 * instead of sending them: no network round trip, no red rows in the database log.
 */
const ANON_AUTH_HEADER = `Bearer ${SUPABASE_PUBLISHABLE_KEY}`;

const consoleFetch: typeof fetch = (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (url.includes('/rest/v1/')) {
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if ((headers.get('authorization') || '') === ANON_AUTH_HEADER) {
      return Promise.resolve(
        new Response(
          JSON.stringify({
            code: 'NO_STAFF_SESSION',
            message: 'Console request skipped: no staff session',
            details: null,
            hint: null,
          }),
          { status: 401, headers: { 'Content-Type': 'application/json' } }
        )
      );
    }
  }
  return fetch(input, init);
};

export const supabaseAdmin = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storageKey: 'sb-admin-auth',
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: { fetch: consoleFetch },
});
